import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Input as AntdInput } from 'antd';
import {
  Input,
  Select,
  Text,
  SelectOption,
  Divider,
  Icon,
  AddressComponent,
  ListingListItem,
  InputTextArea,
  PronounTags,
  PRONOUN_TAGS,
  ContactTypeTags,
  CLIENT_CONTACT_TAGS,
  SUPPLIER_CONTACT_TAGS,
  theme,
  message,
} from 'components';
import { ContactController } from 'api';
import { capitalize } from 'utils';
import { RadioChangeEvent } from 'antd/lib/radio';
import {
  ContactType,
  IClientContact,
  ISupplierContact,
  Pronoun,
  ContactPhoneType,
  ClientContactTag,
  SupplierContactTag,
  IContactPhone,
  IBuyerNotifications,
} from '@bb/contacts';
import FormTypeRadio from './FormTypeRadio';
import { ContactDrawerMode, isSupplier, isClient } from '../../types';
import AddPhone from './Elements/AddPhone';
import AddHomeAddress from './Elements/AddHomeAddress';
import AddListing from './Elements/AddListing';
import AddNotes from './Elements/AddNotes';
import BuyerNotifications from './Elements/BuyerNotifications';
import FormRow from './Elements/FormRow';
import { TagGroupOption } from '../../../../components/TagGroup';
import {
  ClientFormValues,
  ContactFormValues,
  ContactListing,
  FormValues,
  SupplierFormValues,
} from './types';
import {
  CLIENT_ERROR_MESSAGES,
  SUPPLIER_ERROR_MESSAGES,
  DEFAULT_ERRORS,
  SHARED_ERROR_MESSAGES,
  FormErrors,
} from './errors';
import validateFormValue from './validators';
import AddCompanyAddress from './Elements/AddCompanyAddress';
import AddWebsite from './Elements/AddWebsite';
import DrawerFooter from '../DrawerFooter';
import { ContactDrawerContext, LayoutContext } from '../../../../contexts';
import { ContactPayload } from '../../../../api/ContactController/types';
import {
  ACTION_HEADER_OFFSET,
  DRAWER_FOOTER_HEIGHT,
  DRAWER_HEADER_HEIGHT,
  DRAWER_PADDING,
} from '../../constants';

import errorReporter from '../../../../app/errorReporter';

const DEFAULT_BUYER_NOTIFICATIONS = {
  Booked: false,
  Confirmed: true,
  Cancelled: true,
  Deny: false,
  'Time Change': false,
};

interface ContactFormState extends FormValues {
  errors: FormErrors;
  type: ContactType;
  submitting: boolean;
  dirty: Partial<Record<keyof FormValues, true>>;
}

interface ContactFormProps {
  mode: ContactDrawerMode;
  contact?: ISupplierContact | IClientContact | undefined;
  onCancel?: () => void;
}

const DEFAULT_PHONE: IContactPhone = {
  type: ContactPhoneType.Mobile,
  number: '',
};

class ContactForm extends React.Component<ContactFormProps, ContactFormState> {
  declare context: React.ContextType<typeof ContactDrawerContext>;

  public constructor(props: ContactFormProps) {
    super(props);

    const { contact } = props;

    this.state = {
      clientTags: [],
      companyName: '',
      email: contact?.email || '',
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      notes: contact?.notes || undefined,
      phones: contact?.phones || [{ ...DEFAULT_PHONE }],
      pronoun: contact?.pronoun?.length
        ? contact.pronoun.map(key => PRONOUN_TAGS[key]).filter(Boolean)
        : [],
      listings: [],
      supplierTags: [],
      type: contact?.type || ContactType.Client,
      submitting: false,
      dirty: {},
      errors: {
        ...DEFAULT_ERRORS,
      },
      buyerNotifications: {
        ...DEFAULT_BUYER_NOTIFICATIONS,
      },
    };

    if (contact) {
      if (isSupplier(contact)) {
        this.state = {
          ...this.state,
          companyAddress: contact.companyAddress || undefined,
          companyName: contact.companyName || '',
          supplierTags:
            contact.tags
              .map(tag => SUPPLIER_CONTACT_TAGS[tag])
              .filter(Boolean) || [],
          type: contact.type,
          website: contact.website || undefined,
        };
      }
      if (isClient(contact)) {
        this.state = {
          ...this.state,
          address: contact.address || undefined,
          clientTags:
            contact.tags.map(tag => CLIENT_CONTACT_TAGS[tag]).filter(Boolean) ||
            [],
          companyAddress: undefined,
          companyName: undefined,
          listings: contact.listings || [],
          website: undefined,
          buyerNotifications: contact.buyerNotifications,
        };
      }
    }

    this.state = {
      ...this.state,
      errors: this._checkErrors(this.state),
    };
  }

  // #region Setters

  public setContactType = (ev: RadioChangeEvent) => {
    const type = ev.target.value;

    this.setState({ type });
  };

  public setContactTags = (
    tags: TagGroupOption<ClientContactTag | SupplierContactTag>[],
  ) => {
    const { type } = this.state;
    if (type === ContactType.Client) {
      const errorMessage = validateFormValue('clientTags', tags);
      this.setState(prevState => ({
        clientTags: tags as TagGroupOption<ClientContactTag>[],
        errors: {
          ...prevState.errors,
          clientTags: errorMessage,
        },
      }));
      this._setDirty('clientTags');

      if ([...tags].pop()?.value === 'buyer') {
        this.setBuyerNotifications(DEFAULT_BUYER_NOTIFICATIONS);
      }
    }

    if (type === ContactType.Supplier) {
      const errorMessage = validateFormValue('supplierTags', tags);
      this.setState(prevState => ({
        supplierTags: tags as TagGroupOption<SupplierContactTag>[],
        errors: {
          ...prevState.errors,
          supplierTags: errorMessage,
        },
      }));
      this._setDirty('supplierTags');
    }
  };

  public setPronounTags = (tags: TagGroupOption<Pronoun>[]) => {
    this.setState({ pronoun: tags });
  };

  public setPhoneType = (type: ContactPhoneType, index: any) => {
    const { phones } = this.state;
    const newPhones = phones.slice();
    newPhones[index].type = type;
    this.setState({ phones: newPhones });
  };

  public setPhoneValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { errors, phones } = this.state;
    const newPhones = phones.slice();

    newPhones[index].number = event.currentTarget.value;

    const errorMessage = validateFormValue('phones', newPhones, index);

    const newErrors = (errors.phones as string[]).slice();
    newErrors[index] = errorMessage;

    this.setState(prevState => ({
      phones: newPhones,
      errors: {
        ...prevState.errors,
        phones: newErrors,
      },
    }));

    this._setDirty('phones');
  };

  public addPhone = () => {
    const { phones } = this.state;
    if (phones.length > 2) return;

    this.setState({ phones: [...phones, { ...DEFAULT_PHONE }] });
  };

  public removePhone = (index: number) => {
    const { errors, phones } = this.state;
    if (phones.length < 2) return;
    const newPhones = phones.slice();
    const newErrors = (errors.phones as string[]).slice();

    newPhones.splice(index, 1);
    newErrors.splice(index, 1);

    this.setState(prevState => ({
      phones: newPhones,
      errors: {
        ...prevState.errors,
        phones: newErrors,
      },
    }));
  };

  public setCompanyAddress = (result: any) => {
    this.setState({
      companyAddress: result,
    });
  };

  public removeCompanyAddress = () => {
    this.setState({ companyAddress: undefined });
  };

  public setHomeAddress = (result: any) => {
    this.setState({ address: result });
  };

  public removeHomeAddress = () => {
    this.setState({ address: undefined });
  };

  public removeListing = (index: number) => {
    const { listings } = this.state;
    const newListings = listings.slice();

    newListings.splice(index, 1);

    this.setState({ listings: newListings });
  };

  public setListings = (listing: ContactListing) => {
    const newListing: IClientContact['listings'][number] = {
      id: listing.id,
      address: {
        line1: listing.line1,
        line2: listing.line2,
        city: listing.city,
        province: listing.province,
        postalCode: listing.postal_code,
      },
      thumbnail: listing.thumbnail,
      isContact: listing.isContact,
    };

    this.setState(prevState => ({
      listings: [...prevState.listings, newListing],
    }));
  };

  public setFirstName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    const errorMessage = validateFormValue('firstName', value);
    this.setState(prevState => ({
      firstName: value,
      errors: {
        ...prevState.errors,
        firstName: errorMessage,
      },
    }));

    this._setDirty('firstName');
  };

  public setCompanyName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    const errorMessage = validateFormValue('companyName', value);

    this.setState(prevState => ({
      companyName: value,
      errors: {
        ...prevState.errors,
        companyName: errorMessage,
      },
    }));

    this._setDirty('companyName');
  };

  public setLastName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    this.setState({ lastName: value });
  };

  public setEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    const errorMessage = validateFormValue('email', value);

    this.setState(prevState => ({
      email: value,
      errors: {
        ...prevState.errors,
        email: errorMessage,
      },
    }));

    this._setDirty('email');
  };

  public setNotes = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const {
      currentTarget: { value },
    } = event;

    this.setState({ notes: value });
  };

  public setWebsite = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    const errorMessage = validateFormValue('website', value);

    this.setState(prevState => ({
      website: value,
      errors: {
        ...prevState.errors,
        website: errorMessage,
      },
    }));

    this._setDirty('website');
  };

  public setBuyerNotifications = (buyerNotifications: IBuyerNotifications) =>
    this.setState({ buyerNotifications });

  // #endregion Setters

  // #region Util
  private _setDirty = (key: keyof FormValues) => {
    const { dirty } = this.state;
    if (dirty[key]) return;

    this.setState(prevState => ({
      dirty: {
        ...prevState.dirty,
        [key]: true,
      },
    }));
  };

  private _buildContactPayload = () => {
    const { email, firstName, lastName, pronoun, type, notes, phones } =
      this.state;

    // first, trim mask characters off of phone entries if no ext provided
    // ex. "(999) 999-9999 x_" => "(999) 999-9999"
    const editedPhones = phones.map(phone => ({
      ...phone,
      number: phone.number.replace(/ x_/g, ''),
    }));

    if (type === ContactType.Client) {
      const { address, clientTags, listings, buyerNotifications } = this.state;
      const client: ContactPayload<IClientContact> = {
        address,
        email,
        firstName,
        listings: listings.map(({ id, isContact }) => ({ id, isContact })),
        phones: editedPhones.filter(({ number }) => Boolean(number)),
        tags: clientTags.map(({ value }) => value),
        notes,
        lastName,
        pronoun: pronoun.map(({ value }) => value),
        buyerNotifications,
      };

      return client;
    }

    if (type === ContactType.Supplier) {
      const { companyName, companyAddress, supplierTags, website } = this.state;
      const supplier: ContactPayload<ISupplierContact> = {
        companyAddress,
        companyName,
        email,
        website,
        firstName,
        phones: editedPhones.filter(({ number }) => Boolean(number)),
        tags: supplierTags.map(({ value }) => value),
        notes,
        lastName,
        pronoun: pronoun.map(({ value }) => value),
        listings: undefined,
      };

      return supplier;
    }
  };

  private _checkErrors = (state?: ContactFormState) => {
    const { errors } = state || this.state;

    const errorMessages = Object.keys(state || this.state).reduce<FormErrors>(
      (acc, key) => {
        if (key in errors) {
          const formKey = key as keyof FormValues;
          const value = this.state[formKey];

          if (Array.isArray(errors[formKey]) && Array.isArray(value)) {
            acc[formKey] = (value as unknown[]).map(
              (val: any, index: number) => validateFormValue(formKey, val, index),
            );
          } else {
            acc[formKey] = validateFormValue(formKey, value);
          }
        }
        return acc;
      },
      { ...DEFAULT_ERRORS },
    );

    if (!state) {
      this.setState({
        errors: errorMessages,
      });
    }

    return errorMessages;
  };

  private _isInvalid = (errs?: ContactFormState['errors']) => {
    const { errors, dirty, type } = this.state;

    if (!dirty) return true;

    const _errors = errs || errors;

    let isInvalid = false;

    if (type === ContactType.Client) {
      isInvalid = Object.keys({
        ...CLIENT_ERROR_MESSAGES,
        ...SHARED_ERROR_MESSAGES,
      }).some(key => {
        const formKey = key as keyof ClientFormValues | keyof ContactFormValues;
        if (Array.isArray(_errors[formKey])) {
          return (_errors[formKey] as string[]).some(Boolean);
        }
        if (_errors[formKey]) return true;
        return false;
      });
    }

    if (type === ContactType.Supplier) {
      isInvalid = Object.keys({
        ...SUPPLIER_ERROR_MESSAGES,
        ...SHARED_ERROR_MESSAGES,
      }).some(key => {
        const formKey = key as
          | keyof SupplierFormValues
          | keyof ContactFormValues;

        if (Array.isArray(_errors[formKey])) {
          return (_errors[formKey] as string[]).some(Boolean);
        }
        if (_errors[formKey]) return true;
        return false;
      });
    }

    return isInvalid;
  };

  private _submitForm = async () => {
    const { contact, mode } = this.props;

    const { closeContactDrawer, triggerReload } = this.context;

    if (!['add', 'edit'].includes(mode)) return;

    const errors = this._checkErrors();

    if (this._isInvalid(errors)) return;

    this.setState({ submitting: true });

    try {
      const contactPayload = this._buildContactPayload();

      const { type } = this.state;

      if (mode === 'add') {
        await ContactController.createContact<
          IClientContact | ISupplierContact
        >(type, contactPayload);
      }

      if (mode === 'edit' && contact) {
        await ContactController.updateContact<
          IClientContact | ISupplierContact
        >(contact.id, contactPayload);
      }

      // Add timeout so user can see it is saving
      this.setState({ submitting: false }, () => {
        closeContactDrawer();
        triggerReload();
      });
    } catch (err) {
      message.error(`Could not save contact.`);
      errorReporter.error(err);
      this.setState({ submitting: false });
    }
  };

  // #endregion Util

  public render() {
    const {
      address,
      clientTags,
      companyName,
      email,
      firstName,
      phones,
      supplierTags,
      companyAddress,
      lastName,
      listings,
      notes,
      pronoun,
      website,
      type,
      errors,
      submitting,
      dirty,
      buyerNotifications,
    } = this.state;

    const { mode, onCancel } = this.props;
    return (
      <>
        <LayoutContext.Consumer>
          {({ isMobileView, offsetTop }) => (
            <>
              <Container
                mode={mode}
                offsetTop={offsetTop}
                isMobile={isMobileView}
                className="drawer-content-container"
              >
                {mode === 'add' && (
                  <RadioContainer data-cy="contact-type-radios">
                    <FormTypeRadio
                      value={type}
                      onChange={this.setContactType}
                    />
                  </RadioContainer>
                )}
                <FormRow
                  label={`Type${type === ContactType.Client ? '(s)' : ''}`}
                  dataCy="contact-edit-role"
                  required
                >
                  {type === ContactType.Client && (
                    <>
                      <ContactTypeTags
                        type={type}
                        onChange={this.setContactTags}
                        tags={clientTags}
                      />
                      {errors.clientTags && dirty.clientTags && (
                        <ErrorMessage>{errors.clientTags}</ErrorMessage>
                      )}
                    </>
                  )}
                  {type === ContactType.Supplier && (
                    <>
                      <ContactTypeTags
                        type={type}
                        onChange={this.setContactTags}
                        tags={supplierTags}
                        limit={1}
                      />
                      {errors.supplierTags && dirty.supplierTags && (
                        <ErrorMessage>{errors.supplierTags}</ErrorMessage>
                      )}
                    </>
                  )}
                </FormRow>
                {type === 'supplier' && (
                  <>
                    <FormRow label="Company Name" required>
                      <ContactInput
                        value={companyName}
                        error={
                          dirty.companyName ? errors.companyName : undefined
                        }
                        onChange={this.setCompanyName}
                        data-cy="contact-edit-company-name"
                      />
                    </FormRow>
                    <FormRow
                      divider
                      label={
                        companyAddress && typeof companyAddress !== 'string'
                          ? 'Company Address'
                          : undefined
                      }
                    >
                      {companyAddress && typeof companyAddress !== 'string' ? (
                        <AddressContainer>
                          <AddressComponentContainer>
                            <AddressComponent address={companyAddress} />
                          </AddressComponentContainer>
                          <RemoveAddressContainer
                            onClick={this.removeCompanyAddress}
                          >
                            <RemoveAddressIcon type="x" />
                          </RemoveAddressContainer>
                        </AddressContainer>
                      ) : (
                        <AddCompanyAddress
                          onSelectOption={this.setCompanyAddress}
                          onInputChange={this.setCompanyAddress}
                        />
                      )}
                    </FormRow>
                  </>
                )}
                <FormRow label="First Name" required>
                  <ContactInput
                    value={firstName}
                    error={dirty.firstName ? errors.firstName : undefined}
                    onChange={this.setFirstName}
                    data-cy="contact-edit-firstname"
                  />
                </FormRow>

                <FormRow label="Last Name">
                  <ContactInput
                    value={lastName}
                    onChange={this.setLastName}
                    data-cy="contact-edit-lastname"
                  />
                </FormRow>
                <FormRow divider label="Pronouns" dataCy="contact-edit-pronoun">
                  <PronounTags onChange={this.setPronounTags} tags={pronoun} />
                </FormRow>
                <FormRow label="Email">
                  <ContactInput
                    value={email}
                    onChange={this.setEmail}
                    error={dirty.email ? errors.email : undefined}
                    data-cy="contact-edit-email"
                  />
                </FormRow>
                <FormRow label="Phone(s)">
                  {phones.map((item, index) => (
                    <React.Fragment key={index}>
                      <PhoneEntryContainer>
                        <AntdInput.Group style={{ display: 'flex' }}>
                          <Select
                            defaultValue={item.type}
                            bordered={false}
                            style={{
                              fontSize: 12,
                              backgroundColor: theme.gray3,
                              width: 95, // enough to fit the longest type, "primary"
                              borderRight: `1px solid ${theme.gray5}`,
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            }}
                            dropdownStyle={{
                              zIndex: 2001, // one higher than the hard-coded mobile drawer zindex
                            }}
                            onSelect={(event: ContactPhoneType) =>
                              this.setPhoneType(event, index)
                            }
                            data-cy="contact-phone-type-select"
                          >
                            {Object.values(ContactPhoneType).map(phoneType => (
                              <SelectOption key={phoneType} value={phoneType}>
                                {capitalize(phoneType)}
                              </SelectOption>
                            ))}
                          </Select>
                          <ContactInput
                            style={{
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            }}
                            className="full-width-wrapper"
                            value={item.number}
                            onChange={event => this.setPhoneValue(event, index)}
                            phoneNumberMaskWithExtension
                            error={
                              dirty.phones ? errors.phones[index] : undefined
                            }
                            data-cy="contact-edit-phone"
                          />
                          {phones.length > 1 && (
                            <CloseIconContainer
                              onClick={() => this.removePhone(index)}
                            >
                              <Icon
                                type="antdCloseCircleOutlined"
                                height={14}
                                width={14}
                              />
                            </CloseIconContainer>
                          )}
                        </AntdInput.Group>
                      </PhoneEntryContainer>
                    </React.Fragment>
                  ))}
                </FormRow>
                <FormRow>
                  {phones.length < 3 ? (
                    <AddPhone onClick={this.addPhone} />
                  ) : null}
                </FormRow>
                {type === 'client' && (
                  <FormRow
                    label={
                      address && typeof address !== 'string'
                        ? 'Home Address'
                        : undefined
                    }
                    dataCy="contact-edit-address"
                  >
                    {address && typeof address !== 'string' ? (
                      <AddressContainer>
                        <AddressComponentContainer>
                          <AddressComponent address={address} />
                        </AddressComponentContainer>
                        <RemoveAddressContainer
                          onClick={this.removeHomeAddress}
                        >
                          <RemoveAddressIcon type="x" />
                        </RemoveAddressContainer>
                      </AddressContainer>
                    ) : (
                      <AddHomeAddress
                        onSelectOption={this.setHomeAddress}
                        onInputChange={this.setHomeAddress}
                      />
                    )}
                  </FormRow>
                )}
                {type === 'supplier' && (
                  <FormRow
                    label={website !== undefined ? 'Website' : undefined}
                  >
                    {website !== undefined ? (
                      <ContactInput
                        value={website}
                        onChange={event => this.setWebsite(event)}
                        error={dirty.website ? errors.website : undefined}
                        data-cy="contact-edit-website"
                      />
                    ) : (
                      <AddWebsite
                        onClick={() => this.setState({ website: '' })}
                      />
                    )}
                  </FormRow>
                )}
                <Divider />
                {type === 'client' && (
                  <FormRow
                    divider
                    label={listings.length > 0 ? 'Listings' : undefined}
                    dataCy="contact-edit-listing"
                  >
                    {listings?.length > 0 &&
                      listings.map((listing, index) => (
                        <ListingContainer key={listing.id}>
                          <ListingListItem listing={listing} type="simple" />
                          <RemoveListingContainer
                            onClick={() => this.removeListing(index)}
                          >
                            <RemoveListingIcon type="x" />
                          </RemoveListingContainer>
                        </ListingContainer>
                      ))}
                    <AddListing
                      listings={listings}
                      onSelectOption={this.setListings}
                      isBuyer={
                        clientTags.length === 1 &&
                        clientTags[0].value === ClientContactTag.Buyer
                      }
                    />
                  </FormRow>
                )}
                <FormRow
                  divider
                  label={notes !== undefined ? 'Notes' : undefined}
                  dataCy="contact-edit-notes"
                >
                  {notes !== undefined ? (
                    /* TODO: set limit to 200 chars */
                    <InputTextArea
                      value={notes}
                      rows={4}
                      onChange={this.setNotes}
                      data-cy="contact-notes-textarea"
                    />
                  ) : (
                    <AddNotes onClick={() => this.setState({ notes: '' })} />
                  )}
                </FormRow>
                {type === 'client' &&
                  clientTags
                    .map(({ value }) => value)
                    .includes(ClientContactTag.Buyer) && (
                    <FormRow label="Buyer Notifications">
                      <BuyerNotifications
                        value={
                          buyerNotifications || DEFAULT_BUYER_NOTIFICATIONS
                        }
                        onChange={this.setBuyerNotifications}
                      />
                    </FormRow>
                  )}
              </Container>
              <DrawerFooter
                disabled={this._isInvalid()}
                onSave={this._submitForm}
                onCancel={onCancel || this.context.closeContactDrawer}
                working={submitting}
                offsetTop={!isMobileView && offsetTop}
                type={type}
                mode={mode}
              />
            </>
          )}
        </LayoutContext.Consumer>
      </>
    );
  }
}

export default ContactForm;

ContactForm.contextType = ContactDrawerContext;

const Container = styled.div<{
  mode?: ContactDrawerMode;
  isMobile?: boolean;
  offsetTop?: number;
}>`
  ${({ mode }) =>
    ['add', 'edit'].includes(mode)
      ? `margin-top: ${ACTION_HEADER_OFFSET * 2.5}px;
    border-top: 1px solid ${theme.gray4};`
      : ''}
  padding: 0px 2.5px;
  overflow-y: auto;
  ${({ mode, isMobile, offsetTop }) => {
    const actionHeaderOffset = ['add', 'edit'].includes(mode)
      ? 0
      : ACTION_HEADER_OFFSET * 2;
    const mobileOffset = isMobile ? 100 : 0; // this 100 is hardcoded within the drawer component
    return `height: calc(100vh - ${
      DRAWER_HEADER_HEIGHT +
      DRAWER_FOOTER_HEIGHT -
      DRAWER_PADDING +
      actionHeaderOffset +
      mobileOffset +
      offsetTop // assume bar
    }px)`;
  }}
`;

const ErrorMessage = styled(Text).attrs({ type: 'danger' })`
  font-size: 1.2rem;
`;

const RadioContainer = styled.div`
  padding-top: ${ACTION_HEADER_OFFSET}px;
`;

const PhoneEntryContainer = styled.div`
  margin-bottom: 10px;
`;

const CloseIconContainer = styled.div`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0px 7.5px;
  }
`;

const RemoveAddressContainer = styled.div`
  cursor: pointer;
`;

const RemoveAddressIcon = styled(Icon)`
  flex: 1;
`;

const RemoveListingContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddressContainer = styled.div`
  display: flex;
  margin: 10px 0px;
`;

const RemoveListingIcon = styled(Icon)`
  flex: 1;
`;

const ListingContainer = styled.div`
  display: flex;
  margin: 10px 0px;
`;

const AddressComponentContainer = styled.div`
  display: flex;
  flex: 4;
`;

const ContactInput = styled(Input).attrs({
  autoComplete: uuidv4(),
  autocaptialize: 'off',
  position: 'after',
})``;
